import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { fetchClaimInfo, completeClaim, checkNumistrailIdAvailable } from "../api/users";
import { validateNumistrailId, validatePassword, normalizeInput } from "../utils/validation";

const LEGAL_VERSION_TOS = "tos_v5_2026_04";
const LEGAL_VERSION_PRIVACY = "privacy_v4_2026_03";
const LEGAL_VERSION_DISCLAIMER = "disclaimer_v3_2026_03";

export function Claim() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = normalizeInput(query.get("token"));
    const langParam = normalizeInput(query.get("lang"));
  const [claimInfo, setClaimInfo] = useState(null);
  const [form, setForm] = useState({ numistrail_id: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [suggestion, setSuggestion] = useState(null);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalTouched, setLegalTouched] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const idInputRef = useRef(null);

  useEffect(() => {
    if (!langParam) return;
    i18n.changeLanguage(langParam);
  }, [langParam, i18n]);

  useEffect(() => {
    if (!token) {
      setError(t("claim.errors.missingToken"));
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchClaimInfo(token)
      .then((resp) => {
        if (cancelled) return;
        setClaimInfo(resp.data);
      })
      .catch((err) => {
        if (cancelled) return;
        const messageKey = err.response?.data?.message_key;
        const message = err.response?.data?.message;
        setError(
          t(messageKey || "claim.errors.fetchFailed", {
            defaultValue: message || t("claim.errors.fetchFailed"),
          })
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  useEffect(() => {
    if (!claimInfo?.email || form.numistrail_id) return;
    const base = claimInfo.email.split("@")[0] || "";
    const candidate = normalizeInput(base, { lowercase: true }).replace(/[^a-z0-9_]/g, "").slice(0, 64);
    if (candidate) {
      setForm((current) => ({ ...current, numistrail_id: candidate }));
    }
  }, [claimInfo, form.numistrail_id]);

  const handleChange = (key) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    if (key === "numistrail_id") {
      setSuggestion(null);
    }
  };

  const markTouched = (key) => () =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const validationErrors = useMemo(() => ({
    numistrail_id: validateNumistrailId(form.numistrail_id),
    password: validatePassword(form.password),
  }), [form.numistrail_id, form.password]);

  const isFormValid = useMemo(
    () => Object.values(validationErrors).every((message) => !message),
    [validationErrors]
  );

  const suggestUsername = async (base) => {
    let attempt = 0;
    while (attempt < 5) {
      const normalizedBase = normalizeInput(base || "user", { lowercase: true }).replace(/[^a-z0-9_]/g, "").slice(0, 40) || "user";
      const candidate = `${normalizedBase}${Math.floor(Math.random() * 1000)}`;
      try {
        const res = await checkNumistrailIdAvailable(candidate);
        if (res.data.available) return candidate;
      } catch (e) {
        // ignore and try another
      }
      attempt += 1;
    }
    return null;
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((current) => ({ ...current, numistrail_id: suggestion }));
    setSuggestion(null);
    setError(null);
    setTimeout(() => idInputRef.current?.focus(), 0);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      setError(t("claim.errors.missingToken"));
      return;
    }
    if (!isFormValid) {
      setTouched({ numistrail_id: true, password: true });
      setError(t("validation.fixErrors"));
      return;
    }
    if (!legalAccepted) {
      setLegalTouched(true);
      setError(t("validation.legalAcceptance.required"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await completeClaim({
        token,
        numistrail_id: normalizeInput(form.numistrail_id, { lowercase: true }),
        password: normalizeInput(form.password),
        legal_acceptance: true,
        legal_version_tos: LEGAL_VERSION_TOS,
        legal_version_privacy: LEGAL_VERSION_PRIVACY,
        legal_version_disclaimer: LEGAL_VERSION_DISCLAIMER,
      });
      setSuccess(true);
    } catch (err) {
      setSuccess(false);
      const messageKey = err.response?.data?.message_key;
      const message = err.response?.data?.message;
      const resolvedMessage = t(messageKey || "claim.errors.claimFailed", {
        defaultValue: message || t("claim.errors.claimFailed"),
      });
      if (resolvedMessage) {
        setError(resolvedMessage);
        if ((message || "").includes("numistrail_id")) {
          const suggested = await suggestUsername(form.numistrail_id);
          if (suggested) setSuggestion(suggested);
        }
      } else {
        setError(t("claim.errors.claimFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLegalChange = (event) => {
    const checked = event.target.checked;
    setLegalAccepted(checked);
    setLegalTouched(true);
    if (checked && error === t("validation.legalAcceptance.required")) {
      setError(null);
    }
  };

  const items = claimInfo?.items || [];

  return (
    <div className="register-screen">
      <div className="register-card">
        <div className="register-card__header">
          <h2 className="register-title">{t("claim.title")}</h2>
          <p className="register-subtitle">{t("claim.subtitle")}</p>
        </div>

        {error && (
          <div className="register-status register-status--error">{error}</div>
        )}

        {success && (
          <div className="register-status register-status--success">
            <strong>{t("claim.success.title")}</strong>
            <p>{t("claim.success.body")}</p>
            <p>
              <a href="/login">{t("claim.loginLink")}</a>
            </p>
          </div>
        )}

        {suggestion && !success && (
          <div className="register-status register-status--info">
            <span>{t("register.suggestedId")}</span>
            <button type="button" className="register-suggestion" onClick={applySuggestion}>
              {suggestion}
            </button>
          </div>
        )}

        {loading && !claimInfo && !success ? (
          <div className="register-status register-status--info">{t("common.loading")}</div>
        ) : null}

        {!success && (
          <form className="register-form" onSubmit={handleSubmit}>
            <div className="claim-items">
              <h3>{t("claim.items.title")}</h3>
              {items.length ? (
                <ul>
                  {items.map((item) => {
                    const title = item.title || `${item.certification_authority} ${t("claim.items.cert", { number: item.certification_number })}`;
                    return (
                      <li key={`${item.certification_authority}-${item.certification_number}`}>
                        <strong>{title}</strong>
                        <span>{item.certification_authority} - {t("claim.items.cert", { number: item.certification_number })}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="claim-items__empty">{t("claim.items.empty")}</p>
              )}
            </div>

            <div className="register-field">
              <label className="register-label" htmlFor="claim-email">
                {t("claim.email.label")}
              </label>
              <input
                className="register-input"
                id="claim-email"
                type="email"
                value={claimInfo?.email || ""}
                readOnly
                disabled
              />
            </div>

            <div className="register-field">
              <label className="register-label" htmlFor="claim-id">
                {t("claim.numistrail.label")}
              </label>
              <input
                className="register-input"
                id="claim-id"
                placeholder={t("claim.numistrail.placeholder")}
                ref={idInputRef}
                value={form.numistrail_id}
                onChange={handleChange("numistrail_id")}
                onBlur={markTouched("numistrail_id")}
                disabled={loading}
              />
              {touched.numistrail_id && validationErrors.numistrail_id ? (
                <p className="register-field__error" role="alert">
                  {t(validationErrors.numistrail_id)}
                </p>
              ) : null}
            </div>

            <div className="register-field">
              <label className="register-label" htmlFor="claim-password">
                {t("claim.password.label")}
              </label>
              <div className="login-password-input">
                <input
                  className="register-input"
                  id="claim-password"
                  placeholder={t("claim.password.placeholder")}
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange("password")}
                  onBlur={markTouched("password")}
                  autoComplete="new-password"
                  disabled={loading}
                />
                <button
                  type="button"
                  className="login-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  disabled={loading}
                >
                  {showPassword ? "\u{1F648}" : "\u{1F441}\uFE0F"}
                </button>
              </div>
              {touched.password && validationErrors.password ? (
                <p className="register-field__error" role="alert">
                  {t(validationErrors.password)}
                </p>
              ) : null}
            </div>

            <div className="register-legal">
              <input
                id="claim-legal"
                type="checkbox"
                checked={legalAccepted}
                onChange={handleLegalChange}
                onBlur={() => setLegalTouched(true)}
                disabled={loading}
              />
              <label className="register-legal__label" htmlFor="claim-legal">
                <Trans
                  i18nKey="register.legalAcceptance"
                  components={{
                    terms: <a href="/terms" target="_blank" rel="noreferrer" />,
                    privacy: <a href="/privacy" target="_blank" rel="noreferrer" />,
                    disclaimer: <a href="/disclaimer" target="_blank" rel="noreferrer" />,
                  }}
                />
              </label>
            </div>
            {legalTouched && !legalAccepted ? (
              <p className="register-field__error" role="alert">
                {t("validation.legalAcceptance.required")}
              </p>
            ) : null}

            <button
              className="register-submit"
              type="submit"
              disabled={loading}
            >
              {loading ? t("common.loading") : t("claim.submit")}
            </button>

            <p className="claim-personal">{t("claim.personalLink")}</p>
          </form>
        )}
      </div>
    </div>
  );
}

